import { z } from "zod";

export const purchaseOrder = z.object({
  id: z.string(),
  purchaseOrderNumber: z.string(),
  orderDate: z.string(),
  deliveryDate: z.string(),
  deliveryAddress: z.string(),
  status: z.string(),
  contact: z.object({
    id: z.string(),
    businessId: z.string(),
    fullName: z.string(),
    alias: z.string(),
    phoneNumber: z.string(),
    email: z.string(),
    address: z.string(),
    businessName: z.string(),
    businessLogo: z.string(),
  }),
  buyer: z.object({
    businessId: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phoneNumber: z.string(),
    businessName: z.string(),
    businessLogo: z.string(),
  }),
  items: z.array(
    z.object({
      quantity: z.number(),
      product: z.object({
        name: z.string(),
        id: z.string(),
      }),
      variant: z.object({
        name: z.string(),
        id: z.string(),
        price: z.number(),
        unit: z.string().optional(),
        sku: z.string().optional(),
      }),
      id: z.string(),
    }),
  ),
  amount: z.number(),
  notes: z.string(),
  termsAndConditions: z.string(),
  approvals: z.array(
    z.object({
      createdBy: z.string(),
      createdAt: z.string(),
      action: z.string(),
      comment: z.string(),
    }),
  ),
  __v: z.number(),
  outlet: z.object({
    name: z.string(),
    id: z.string(),
  }),
  invoiceId: z.string(),
  netTerms: z.number(),
});

export const purchaseOrders = z.array(purchaseOrder);

export const newPurchaseOrder = z.object({
  contactId: z.string().min(1, { message: "Contact is required" }),
  status: z.string(),
  orderDate: z.string(),
  deliveryDate: z.string(),
  deliveryAddress: z.string(),
  products: z.array(
    z.object({
      name: z.string().min(1, { message: "Product is required" }),
      productId: z.string().min(1, { message: "Product is required" }),
      variant: z.object({
        name: z.string().min(1, { message: "Variant is required" }),
        id: z.string().min(1, { message: "Variant is required" }),
        price: z.number().positive({ message: "Price is required" }),
        unit: z.string().optional(),
        sku: z.string().optional(),
      }),
      quantity: z.number().positive({ message: "Quantity is required" }),
    }),
  ),
  termsAndConditions: z.string().optional(),
  notes: z.string().optional(),
  netTerms: z.number(),
  outletId: z.string().optional(),
});

export const draftPurchaseOrder = z.object({
  contactId: z.string().min(1, { message: "Contact is required" }),
  status: z.string().includes("DRAFT"),
  orderDate: z.string().optional(),
  deliveryDate: z.string().optional(),
  deliveryAddress: z.string().optional(),
  products: z
    .array(
      z.object({
        name: z.string().optional(),
        productId: z.string().optional(),
        quantity: z.number().optional(),
        variant: z.object({
          name: z.string().optional(),
          id: z.string().optional(),
          price: z.number().optional(),
          unit: z.string().optional(),
          sku: z.string().optional(),
        }),
      }),
    )
    .optional(),
  termsAndConditions: z.string().optional(),
  notes: z.string().optional(),
  netTerms: z.number().optional(),
  outletId: z.string().optional(),
});

export type PurchaseOrder = z.infer<typeof purchaseOrder>;
export type PurchaseOrders = z.infer<typeof purchaseOrders>;
export type NewPurchaseOrder = z.infer<typeof newPurchaseOrder>;
export type DraftPurchaseOrder = z.infer<typeof draftPurchaseOrder>;

export type PurchaseOrderItem = {
  quantity: number;
  id: string;
  product: {
    id: string;
    name: string;
  };
  variant: {
    id: string;
    name: string;
    price: number;
    unit?: string;
    sku?: string;
  };
};

export type NewPurchaseOrderItem = {
  name: string;
  quantity: number;
  productId: string;
  product: {
    id: string;
    name: string;
  };
  variant: {
    id: string;
    name: string;
    price: number;
    unit?: string;
    sku?: string;
  };
  [key: string]: string | number | object;
};

export enum PurchaseOrderStatus {
  SENT = "SENT",
  DRAFT = "DRAFT",
  VIEWED = "VIEWED",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
  EXPIRED = "EXPIRED",
  VOID = "VOID",
}

export type PurchaseOrderApproval = {
  createdBy: string;
  createdAt: string;
  action: string;
  comment: string;
};

export type NewPurchaseOrderApproval = {
  action: string;
  comment: string;
};

export enum PurchaseOrderApprovalStatus {
  ACCEPT = "ACCEPT",
  REJECT = "REJECT",
  COMMENT = "COMMENT",
}
